"use client";
import { Filter, Plus, Search } from "lucide-react";
import { useState } from "react";
import { useTransactionContext } from "./TransactionContext";
import { useCategories } from "@/hooks/useCategories";
import { AddCategoryModal } from "@/components/categories/AddCategoryModal";

export function TransactionFilters() {
  const { filters, updateFilter } = useTransactionContext();
  const { categories, createCategory } = useCategories();
  const [addOpen, setAddOpen] = useState(false);
  const topLevelCategories = categories.filter(
    (category) => category.parentCategoryId === null,
  );
  const selectedCategoryChildren =
    filters.categoryId && filters.categoryId !== "uncategorized"
      ? categories.filter(
          (category) => category.parentCategoryId === filters.categoryId,
        )
      : [];

  return (
    <div className="border-b border-[#e5ebf2] px-3 py-2 sm:px-4">
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#717182]" />
          <input
            type="text"
            placeholder="Search..."
            value={filters.search ?? ""}
            onChange={(e) =>
              updateFilter("search", e.target.value || undefined)
            }
            className="h-9 w-1/3 rounded-md border border-[#d9e1ea] bg-[#f5f7fa] py-2 pl-9 pr-3 text-sm text-[#2d3f57] placeholder:text-[#8b9bb1] outline-none ring-blue-500 focus:ring-2"
          />
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <label className="text-sm text-[#52667f]">Type:</label>
          <div className="relative flex min-w-[190px] items-center">
            <Filter className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#717182]" />
            <select
              value={filters.type ?? ""}
              onChange={(e) =>
                updateFilter(
                  "type",
                  (e.target.value as "income" | "expense") || undefined,
                )
              }
              className="w-full appearance-none rounded-md bg-[#f5f7fa] py-2 pl-9 pr-9 text-[11px] font-black font-sans text-[#2d3f57] outline-none ring-blue-500 focus:ring-2"
            >
              <option value="">Cash In & Cash Out</option>
              <option value="income">Cash In</option>
              <option value="expense">Cash Out</option>
            </select>
          </div>

          <label className="text-sm text-[#52667f]">Categories:</label>
          <div className="relative flex min-w-[196px] items-center">
            <select
              value={filters.categoryId ?? ""}
              onChange={(e) => {
                updateFilter("subcategoryId", undefined);
                updateFilter("categoryId", e.target.value || undefined);
              }}
              className="w-full appearance-none rounded-md bg-[#f5f7fa] py-2 pl-3 pr-9 text-sm font-medium text-[#2d3f57] outline-none ring-blue-500 focus:ring-2"
              aria-label="Category filter"
            >
              <option value="">All Categories</option>
              <option value="uncategorized">Uncategorized</option>
              {topLevelCategories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {selectedCategoryChildren.length > 0 ? (
            <>
              <label className="text-sm text-[#52667f]">Subcategory:</label>
              <div className="relative flex min-w-[196px] items-center">
                <select
                  value={filters.subcategoryId ?? ""}
                  onChange={(e) =>
                    updateFilter("subcategoryId", e.target.value || undefined)
                  }
                  className="w-full appearance-none rounded-md bg-[#f5f7fa] py-2 pl-3 pr-9 text-sm font-medium text-[#2d3f57] outline-none ring-blue-500 focus:ring-2"
                  aria-label="Subcategory filter"
                >
                  <option value="">All Subcategories</option>
                  {selectedCategoryChildren.map((subcategory) => (
                    <option key={subcategory.id} value={subcategory.id}>
                      {subcategory.name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          ) : null}

          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[#d9e1ea] bg-[#f8fafc] text-[#5b6f87] hover:bg-slate-100"
            aria-label="Add category"
            title="Add category"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>
      <AddCategoryModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        categories={categories}
        onCreate={(name, color, type, parentCategoryId) =>
          createCategory(name, color, type, parentCategoryId)
        }
      />
    </div>
  );
}
